const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000/api/v1";

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
let refreshDenied = false;
let onUnauthorized: (() => void) | null = null;
let onWriteBlocked: (() => void) | null = null;

// Telas do app que o visitante sem conta pode navegar em modo demonstração. Sem sessão, as
// leituras desses caminhos são respondidas com dados de exemplo gerados no navegador
// (demo/demoApi.ts) e nem saem pra rede.
const DEMO_PATHS = /^\/(accounts|categories|transactions|credit-cards|goals|budgets|bills|dashboard)(\/|\?|$)/;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setOnUnauthorized(handler: () => void) {
  onUnauthorized = handler;
}

/** Chamado quando uma gravação é barrada: visitante sem conta, ou conta sem assinatura (402). */
export function setOnWriteBlocked(handler: (() => void) | null) {
  onWriteBlocked = handler;
}

export class ApiError extends Error {
  status: number;
  fieldErrors?: Record<string, string[] | undefined>;

  constructor(message: string, status: number, fieldErrors?: Record<string, string[] | undefined>) {
    super(message);
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then(async (res) => {
        refreshDenied = res.status === 401;
        if (!res.ok) return null;
        const data = await res.json();
        accessToken = data.accessToken;
        return data.accessToken as string;
      })
      .catch(() => {
        refreshDenied = false;
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  skipAuthRetry?: boolean;
}

export async function apiFetch<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuthRetry, headers, ...rest } = options;
  const isFormData = body instanceof FormData;
  const isRead = (rest.method ?? "GET").toUpperCase() === "GET";

  if (!accessToken && DEMO_PATHS.test(path)) {
    if (!isRead) {
      onWriteBlocked?.();
      throw new ApiError("Crie sua conta pra começar a anotar.", 401);
    }
    const { demoResponse } = await import("@/demo/demoApi");
    return demoResponse(path) as T;
  }

  const doFetch = async () =>
    fetch(`${API_URL}${path}`, {
      ...rest,
      credentials: "include",
      headers: {
        ...(body !== undefined && !isFormData ? { "Content-Type": "application/json" } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body !== undefined ? (isFormData ? (body as FormData) : JSON.stringify(body)) : undefined,
    });

  let response = await doFetch();

  if (response.status === 401 && !skipAuthRetry && !path.startsWith("/auth/")) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await doFetch();
    } else {
      onUnauthorized?.();
    }
  }

  // Rede de segurança do paywall: se alguma gravação chegar no servidor sem assinatura, a
  // pessoa vai pra tela de pagamento. Só pra escrita - leitura nunca deve redirecionar.
  if (response.status === 402 && !isRead) {
    onWriteBlocked?.();
  }

  if (!response.ok) {
    let message = "Ocorreu um erro inesperado.";
    let fieldErrors: Record<string, string[] | undefined> | undefined;
    try {
      const data = await response.json();
      message = data.message ?? message;
      fieldErrors = data.errors;
    } catch {
      // resposta sem corpo JSON
    }
    throw new ApiError(message, response.status, fieldErrors);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

/**
 * A última tentativa de renovar a sessão falhou porque o servidor disse que não há sessão (401)?
 * Falso quando falhou por rede/servidor fora do ar - aí a sessão pode muito bem ainda existir.
 */
export function wasRefreshDenied() {
  return refreshDenied;
}

export { refreshAccessToken };
